import React, { useMemo, useEffect, useRef } from 'react';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { DispatchDeliverySummaryFilters, IdOption } from '../hooks/useDispatchDeliverySummary';
import { useWarehouseContext } from '../../../../../contexts/WarehouseContext';
import { SiteType } from '../../../../../graphql/types/SiteType';
import { useCurrentDsp } from '../../../../../hooks/useCurrentDsp';
import {
    GET_DISPATCH_DELIVERY_SUMMARY_FILTER_OPTIONS,
    DispatchDeliverySummaryFilterOptionsResponse
} from '../graphql/queries/getDispatchDeliverySummary';
import { getSmartLabelClassName } from '../../../../../utils/labelWidthUtils';
import DayjsRangePickerFilterItem from '../../../../delivery/components/DayjsRangePickerFilterItem';
import MultiIdSelectFilterItem from '../../../../delivery/components/MultiIdSelectFilterItem';

// 配置dayjs ISO周插件
dayjs.extend(isoWeek);
dayjs.extend(isoWeeksInYear);

interface Props {
    filters: DispatchDeliverySummaryFilters;
    onFiltersChange: (next: Partial<DispatchDeliverySummaryFilters>) => void;
    dimension: 'DAY' | 'WEEK' | 'MONTH';
}

const DispatchDeliverySummaryFilter: React.FC<Props> = ({ filters, onFiltersChange, dimension }) => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const { dspId: currentDspId, dsp: currentDsp, isDspManager: isDspUser } = useCurrentDsp();
    // 判断权限类型
    const isHub = currentWarehouse?.siteType === SiteType.HUB;
    const isWHS = currentWarehouse?.siteType === SiteType.WAREHOUSE;
    const isDSP = isDspUser;
    
    // 获取当前 hubId（用于 HUB 权限）
    const currentHubId = useMemo(() => {
        if (currentWarehouse) {
            if (currentWarehouse.siteType === SiteType.HUB) {
                return Number(currentWarehouse.id);
            }
            if (currentWarehouse.siteType === SiteType.WAREHOUSE && currentWarehouse.parentSiteId) {
                return Number(currentWarehouse.parentSiteId);
            }
        }
        return null;
    }, [currentWarehouse]);
    
    // 获取当前 WHS ID（用于 WHS 权限）
    const currentWarehouseId = useMemo(() => {
        if (isWHS && currentWarehouse?.siteType === SiteType.WAREHOUSE) {
            return Number(currentWarehouse.id);
        }
        return null;
    }, [isWHS, currentWarehouse]);
    
    // 构建查询变量
    const filterOptionsVariables = useMemo(() => {
        if (isHub && currentHubId) {
            return { hubId: currentHubId };
        }
        if (isWHS && currentWarehouseId) {
            return { whsId: currentWarehouseId };
        }
        if (isDSP && currentDspId) {
            return { dspId: Number(currentDspId) };
        }
        return {};
    }, [isHub, isWHS, isDSP, currentHubId, currentWarehouseId, currentDspId]);
    
    // 获取筛选选项（只在进入页面时获取一次）
    const shouldFetchFilterOptions =
        !isDSP && Object.keys(filterOptionsVariables).length > 0;
    
    const { data: optionsData, loading: optionsLoading } = useQuery<DispatchDeliverySummaryFilterOptionsResponse>(
        GET_DISPATCH_DELIVERY_SUMMARY_FILTER_OPTIONS,
        {
            variables: filterOptionsVariables,
            skip: !shouldFetchFilterOptions,
            fetchPolicy: 'cache-first'
        }
    );
    
    // 使用 ref 跟踪是否已经初始化过，避免重复设置
    const hasInitializedDSP = useRef(false);
    const prevDspIdRef = useRef<number | null>(null);
    
    // 注意：WHS 权限的 warehouse 选项初始化已经在 hook 中完成，这里不再需要设置
    
    // DSP权限：自动设置当前DSP ID（只设置一次，且只在 dspId 真正变化时）
    useEffect(() => {
        if (isDSP && currentDspId) {
            const dspIdNum = Number(currentDspId);
            if (!hasInitializedDSP.current || prevDspIdRef.current !== dspIdNum) {
                const dspLabel = currentDsp?.name ?? `DSP #${dspIdNum}`;
                if (!filters.dspOptions || !filters.dspOptions.some(opt => opt.value === dspIdNum)) {
                    onFiltersChange({ dspOptions: [{ value: dspIdNum, label: dspLabel }] });
                    hasInitializedDSP.current = true;
                    prevDspIdRef.current = dspIdNum;
                }
            }
        }
    }, [isDSP, currentDspId, currentDsp, filters.dspOptions, onFiltersChange]);
    
    // WHS选项（HUB权限和DSP权限显示）- 过滤掉已选中的选项
    const whsOptions = useMemo(() => {
        if (!optionsData?.getDispatchDeliverySummaryFilterOptions?.warehouses) return [];
        const allOptions = optionsData.getDispatchDeliverySummaryFilterOptions.warehouses.map(whs => ({
            label: whs.name,
            value: whs.id
        }));
        // 过滤掉已选中的选项（确保类型匹配）
        if (!filters.warehouseOptions || filters.warehouseOptions.length === 0) {
            return allOptions;
        }
        const selectedValues = new Set(filters.warehouseOptions.map(opt => Number(opt.value)));
        return allOptions.filter(opt => {
            const optValue = Number(opt.value);
            return !selectedValues.has(optValue);
        });
    }, [optionsData, filters.warehouseOptions]);
    
    // DSP选项（HUB权限和WHS权限显示）- 过滤掉已选中的选项
    const dspOptions = useMemo(() => {
        if (!optionsData?.getDispatchDeliverySummaryFilterOptions?.dsps) return [];
        const allOptions = optionsData.getDispatchDeliverySummaryFilterOptions.dsps.map(dsp => ({
            label: dsp.name,
            value: dsp.id
        }));
        // 过滤掉已选中的选项（确保类型匹配）
        if (!filters.dspOptions || filters.dspOptions.length === 0) {
            return allOptions;
        }
        const selectedValues = new Set(filters.dspOptions.map(opt => Number(opt.value)));
        return allOptions.filter(opt => {
            const optValue = Number(opt.value);
            return !selectedValues.has(optValue);
        });
    }, [optionsData, filters.dspOptions]);
    
    // 处理日期/周/月选择器变化
    const handleDateChange = (range: [Dayjs, Dayjs] | null) => {
        onFiltersChange({ dateRange: range });
    };

    // 获取日期范围标签文本
    const getDateRangeLabel = () => {
        if (dimension === 'WEEK') {
            return t('wms.delivery.dispatchDeliverySummary.filters.weekRange');
        } else if (dimension === 'MONTH') {
            return t('wms.delivery.dispatchDeliverySummary.filters.monthRange');
        } else {
            return t('wms.delivery.dispatchDeliverySummary.filters.dateRange');
        }
    };

    
    return (
        <div className="mb-2 pl-0 py-2">
            <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1">
                    {/* 使用 Grid 布局确保列对齐 */}
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        {/* 日期范围 */}
                        <div className="min-w-0">
                            <DayjsRangePickerFilterItem
                                label={getDateRangeLabel()}
                                value={filters.dateRange}
                                onChange={handleDateChange}
                                spanClassName={getSmartLabelClassName([getDateRangeLabel()])}
                                picker={dimension === 'WEEK' ? 'week' : dimension === 'MONTH' ? 'month' : 'date'}
                                format={
                                    dimension === 'WEEK'
                                        ? (value) => {
                                            const weekYear = value.isoWeekYear();
                                            const week = value.isoWeek();
                                            return `${weekYear}-${String(week).padStart(2, '0')}`;
                                        }
                                        : dimension === 'MONTH'
                                            ? 'YYYY-MM'
                                            : 'YYYY-MM-DD'
                                }
                                disabledDate={(current) => {
                                    if (dimension === 'WEEK') return current > dayjs().endOf('week');
                                    if (dimension === 'MONTH') return current > dayjs().endOf('month');
                                    return current > dayjs().endOf('day');
                                }}
                            />
                        </div>
                        {/* WHS筛选框 */}
                        {isHub && !isDSP && (
                            <div className="min-w-0">
                                <MultiIdSelectFilterItem
                                    label="WHS"
                                    options={whsOptions as any}
                                    value={filters.warehouseOptions}
                                    onChange={(next) => onFiltersChange({ warehouseOptions: next })}
                                    className="w-full"
                                    spanClassName={getSmartLabelClassName(['WHS'])}
                                    placeholder={t('wms.delivery.dispatchDeliverySummary.filters.selectWHS') || undefined}
                                    loading={optionsLoading}
                                />
                            </div>
                        )}
                        {/* DSP筛选框 */}
                        {(isHub || isWHS) && !isDSP && (
                            <div className="min-w-0">
                                <MultiIdSelectFilterItem
                                    label="DSP"
                                    options={dspOptions as any}
                                    value={filters.dspOptions}
                                    onChange={(next) => onFiltersChange({ dspOptions: next })}
                                    className="w-full"
                                    spanClassName={getSmartLabelClassName(['DSP'])}
                                    placeholder={t('wms.delivery.dispatchDeliverySummary.filters.selectDSP') || undefined}
                                    loading={optionsLoading}
                                />
                            </div>
                        )}
                        {/* 如果只有日期范围，添加占位符 */}
                        {((isDSP) || (!isHub && !isWHS)) && (
                            <>
                                <div className="min-w-0"></div>
                                <div className="min-w-0"></div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DispatchDeliverySummaryFilter;
